import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Copy, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

export function CopyFoodModal({
  isOpen,
  onClose,
  entry,
  mealTimes,
  onCopy,
}) {
  const [targetDate, setTargetDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [targetMealTime, setTargetMealTime] = useState('breakfast');

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date().toISOString().split('T')[0];
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];
    
    if (dateStr === today) return 'Today';
    if (dateStr === tomorrowStr) return 'Tomorrow';
    
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${dayNames[date.getDay()]}, ${monthNames[date.getMonth()]} ${date.getDate()}`;
  };

  const changeDate = (days) => {
    const date = new Date(targetDate);
    date.setDate(date.getDate() + days);
    setTargetDate(date.toISOString().split('T')[0]);
  };

  const handleCopy = () => {
    onCopy(entry, targetDate, targetMealTime);
    onClose();
  };

  if (!entry) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Copy className="h-5 w-5 text-blue-400" />
            Copy Food Entry
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Entry Info */}
          <div className="p-3 bg-slate-800 rounded-lg">
            <p className="text-white font-medium">{entry.name}</p>
            <p className="text-slate-400 text-sm">
              {entry.calories} cal • P:{entry.protein}g C:{entry.carbs}g F:{entry.fat}g
            </p>
          </div>

          {/* Target Date */}
          <div>
            <label className="text-sm text-slate-400 block mb-2">Copy to date:</label>
            <div className="flex items-center justify-between bg-slate-800 rounded-lg p-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => changeDate(-1)}
                className="text-slate-400 hover:text-white"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-blue-400" />
                <span className="text-white font-medium">{formatDate(targetDate)}</span>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => changeDate(1)}
                className="text-slate-400 hover:text-white"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Target Meal Time */}
          <div>
            <label className="text-sm text-slate-400 block mb-2">Meal time:</label>
            <div className="grid grid-cols-2 gap-2">
              {mealTimes.map(mt => (
                <button
                  key={mt.id}
                  onClick={() => setTargetMealTime(mt.id)}
                  className={`p-2 rounded-lg text-sm transition-colors ${
                    targetMealTime === mt.id
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  {mt.icon || '🍽️'} {mt.name}
                </button>
              ))}
            </div>
          </div>

          {/* Copy Button */}
          <Button
            onClick={handleCopy}
            className="w-full bg-blue-600 hover:bg-blue-500"
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy to {formatDate(targetDate)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CopyFoodModal;
